import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import React from 'react'
import { addDays } from 'date-fns'
import { useParams } from 'react-router-dom'
import { ViolationRowItem } from './Table/ViolationRowItem'

import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { useViolations } from '@/modules/drivers/hooks/useViolations'
import { getGroupedViolationsColumnsNew } from '@/modules/drivers/constants/tables'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { useDate } from '@/modules/drivers/stores/dateStore'
import useViolationService from '@/modules/drivers/services/ViolationService'
import type { GroupedViolationModel } from '@/app/models/groupedViolationModel'
import { useSideSelectionStore } from '@/modules/drivers/stores/sideSelectionStore'
import ViolationsGroupedFooterBar from './ViolationsGroupedFooterBar'

const ViolationsGrupedTable = () => {
  const { t } = useLocalize()
  const columns = getGroupedViolationsColumnsNew(t)
  const { search, isLoading } = useViolations()
  const { sideCheckedIds } = useSideSelectionStore()
  const { basicDrivers } = useDrivers()
  const { companyId } = useParams<{ companyId: string }>()
  const { range } = useDate()
  const { getGroupedViolations } = useViolationService()

  const [cachedViolations, setCachedViolations] = React.useState<
    GroupedViolationModel[]
  >([])
  const fetchedIdsRef = React.useRef<Set<number>>(new Set())
  const [pendingRequests, setPendingRequests] = React.useState(0)

  const getGroupedViolationsRef = React.useRef(getGroupedViolations)
  React.useEffect(() => {
    getGroupedViolationsRef.current = getGroupedViolations
  }, [getGroupedViolations])

  const startKey = range?.startDate?.fullDate ?? null
  const endKey = range?.endDate?.fullDate ?? null

  React.useEffect(() => {
    setCachedViolations([])
    fetchedIdsRef.current = new Set()
  }, [companyId, startKey, endKey])

  React.useEffect(() => {
    if (!companyId || !range?.startDate || !range?.endDate) return undefined
    const newIds = sideCheckedIds.filter(
      (id) => !fetchedIdsRef.current.has(id),
    )
    if (newIds.length === 0) return undefined

    const fetched = fetchedIdsRef.current
    newIds.forEach((id) => fetched.add(id))
    // Zmiana zaznaczenia nie unieważnia odpowiedzi, inaczej kierowca zostaje z zerami.
    const isStale = () => fetchedIdsRef.current !== fetched

    const startDate = new Date(
      range.startDate.year!,
      range.startDate.month! - 1,
      range.startDate.day!,
    )
    const endDate = new Date(
      range.endDate.year!,
      range.endDate.month! - 1,
      range.endDate.day!,
    )

    setPendingRequests((n) => n + 1)
    getGroupedViolationsRef
      .current({
        companyId,
        drivers: newIds,
        startTime: startDate,
        endTime: addDays(endDate, 1),
      })
      .then((res) => {
        if (isStale()) return
        setCachedViolations((prev) => {
          const map = new Map<number, GroupedViolationModel>()
          for (const v of prev) {
            if (v.driverId != null) map.set(Number(v.driverId), v)
          }
          for (const v of res) {
            if (v.driverId != null) map.set(Number(v.driverId), v)
          }
          return Array.from(map.values())
        })
      })
      .catch((err) => {
        if (!isStale()) newIds.forEach((id) => fetched.delete(id))
        console.error('Error fetching grouped violations:', err)
      })
      .finally(() => setPendingRequests((n) => n - 1))
  }, [sideCheckedIds, companyId, startKey, endKey])

  const displayedViolations = React.useMemo(() => {
    if (sideCheckedIds.length === 0) return []

    return sideCheckedIds.map((driverId) => {
      const existing = cachedViolations.find(
        (v) => Number(v.driverId) === driverId,
      )
      if (existing) return existing

      const driver = basicDrivers.find((d) => d.id === driverId)
      return {
        driverId,
        driver: driver
          ? {
              id: driverId,
              surname: driver.fullName.split(' ')[0],
              name: driver.fullName.split(' ').slice(1).join(' '),
            }
          : { id: driverId, surname: '', name: '' },
        fullName: driver?.fullName ?? String(driverId),
        driverCharge: 0,
        companyCharge: 0,
        amount_NN: 0,
        amount_BPN: 0,
        amount_PN: 0,
      }
    })
  }, [sideCheckedIds, cachedViolations, basicDrivers])

  const { sortedData, handleHeaderClick, sortConfig } = useSortableData({
    data: displayedViolations,
    tableColumns: columns,
  })

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
      {isLoading ? (
        <div className="tw-grid tw-h-full tw-place-items-center">
          <LoaderIcon className="tw-w-16 tw-animate-spin" />
        </div>
      ) : displayedViolations.length > 0 ? (
        <>
          <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-y-auto">
            <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
              <TableHeader
                opacity
                sticky
                rounded={false}
                disabledCheckboxToggle
                columns={columns}
                sortConfig={sortConfig}
                onHeaderClick={handleHeaderClick}
              />

              <tbody>
                {sortedData.map((violation, i) => (
                  <ViolationRowItem
                    key={violation?.driverId}
                    violation={violation}
                    search={search}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <ViolationsGroupedFooterBar
            violations={displayedViolations}
            isLoading={pendingRequests > 0}
          />
        </>
      ) : (
        <>
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
              opacity
              columns={columns}
              disabledCheckboxToggle
              sortConfig={null}
            />
          </table>
          <TableEmpty
            title={
              !search
                ? t('driverModule.no-drivers')
                : t('common.no-search-results')
            }
          />
        </>
      )}
    </section>
  )
}

export default ViolationsGrupedTable
